import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    ButtonBase,
    Chip,
    CircularProgress,
    makeStyles,
    Paper,
    Popover,
    TablePagination,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import HomeWorkIcon from '@material-ui/icons/HomeWork'
import VisibilityIcon from '@material-ui/icons/Visibility'
import WorkIcon from '@material-ui/icons/Work'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import DescriptionIcon from '@material-ui/icons/Description'
import { DocumentPreviewModal } from '../../../ContractManager/DocumentPreviewModal'
import { formatDate as formatCalendarDate } from '../../../ContractManager/utils'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
    CatalogSelectField,
    FilterChipItem,
    PropertyTagFilterField,
    getPropertyTagChip,
} from '../../../ContractManager/CatalogFilterChrome'
import { useOrganizationContractTypeCoverage } from '../../../../hooks/useOrganizationContractTypeCoverage'
import { useOrganizationCertificateTypeCoverage } from '../../../../hooks/useOrganizationCertificateTypeCoverage'
import { useOrganizationDocumentTypeCoverage } from '../../../../hooks/useOrganizationDocumentTypeCoverage'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import {
    OrganizationContractTypeCoverage,
    OrganizationDocumentTypeCoverage,
    OrganizationContractTypeCoverageDocument,
    OrganizationDocumentTypeCoverageDocument,
    ContractLifecycleStatus,
} from '../../../../models/Company/types'

type RowStatus = 'missing' | 'expired' | 'expiring' | 'auto_renew' | 'on_file'
type OverviewFocus =
    | 'all'
    | 'attention'
    | 'missing'
    | 'expired'
    | 'expiring'
    | 'auto_renew'
    | 'current'
type HorizonWindow = 30 | 60 | 90
type OverviewCategory = 'all' | 'contract' | 'certificate' | 'document'

const FOCUS_LABELS: Record<OverviewFocus, string> = {
    all: 'Any status',
    attention: 'Needs attention',
    missing: 'Missing',
    expired: 'Expired',
    expiring: 'Expiring soon',
    auto_renew: 'Auto-renew',
    current: 'Fully current',
}

const COMPLIANCE_TOOLTIP =
    'Required items still in effect, including auto-renew. Missing, expired, and expiring soon are left out.'

const CATEGORY_LABELS: Record<OverviewCategory, string> = {
    all: 'All requirements',
    contract: 'Contracts',
    certificate: 'Certificates',
    document: 'Documents',
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(6),
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    headerTitle: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        minWidth: 240,
        flex: '1 1 320px',
    },
    title: {
        fontWeight: 600,
        letterSpacing: '-0.02em',
    },
    subtitle: {
        maxWidth: 640,
        lineHeight: 1.5,
    },
    headerActions: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: theme.spacing(0.75),
    },
    actionLabel: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    actionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },
    createButton: {
        textTransform: 'none',
        fontWeight: 600,
        backgroundColor: theme.palette.background.paper,
    },
    overview: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: theme.spacing(1.5),
        width: '100%',
    },
    statCard: {
        flex: '1 1 140px',
        minWidth: 140,
        padding: theme.spacing(1.5, 1.75),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        boxShadow: 'inset 0 3px 0 #e2e8f0',
        '&:hover': {
            borderColor: theme.palette.grey[400],
        },
    },
    statCardSelected: {
        borderColor: theme.palette.primary.main,
    },
    statAccentSuccess: {
        boxShadow: 'inset 0 3px 0 #16a34a',
    },
    statAccentWarning: {
        boxShadow: 'inset 0 3px 0 #d97706',
    },
    statAccentDanger: {
        boxShadow: 'inset 0 3px 0 #dc2626',
    },
    statRenew: {
        color: '#1d4ed8',
    },
    statAccentRenew: {
        boxShadow: 'inset 0 3px 0 #2563eb',
    },
    statValue: {
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.1,
    },
    statLabel: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginTop: theme.spacing(0.5),
    },
    statHint: {
        marginTop: theme.spacing(0.25),
        fontSize: 11,
        color: theme.palette.text.secondary,
    },
    statSuccess: {
        color: '#15803d',
    },
    statWarning: {
        color: '#b45309',
    },
    statDanger: {
        color: '#b91c1c',
    },
    banner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        padding: theme.spacing(1.25, 1.75),
        borderRadius: theme.spacing(1.5),
        border: '1px solid #fecaca',
        backgroundColor: '#fef2f2',
    },
    bannerWarning: {
        borderColor: '#fde68a',
        backgroundColor: '#fffbeb',
    },
    bannerCopy: {
        fontWeight: 600,
        color: '#7f1d1d',
    },
    bannerCopyWarning: {
        color: '#78350f',
    },
    textButton: {
        textTransform: 'none',
        fontWeight: 600,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    pagination: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
    },
    emptyState: {
        padding: theme.spacing(6, 3),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
        borderRadius: theme.spacing(1.5),
    },
    emptyIcon: {
        fontSize: 40,
        color: theme.palette.text.disabled,
        marginBottom: theme.spacing(0.5),
    },
    accordion: {
        border: `1px solid ${theme.palette.divider}`,
        borderLeftWidth: 4,
        boxShadow: 'none',
        borderRadius: theme.spacing(1.5),
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        '&:before': {
            display: 'none',
        },
        '&.Mui-expanded': {
            margin: 0,
        },
        '& .MuiAccordionSummary-root': {
            padding: theme.spacing(0.5, 2, 0.5, 1.5),
        },
        '& .MuiAccordionSummary-content': {
            margin: theme.spacing(1.25, 0),
            alignItems: 'center',
            minWidth: 0,
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: theme.spacing(1.25, 0),
        },
    },
    accordionDanger: {
        borderLeftColor: '#dc2626',
    },
    accordionWarning: {
        borderLeftColor: '#d97706',
    },
    accordionComplete: {
        borderLeftColor: '#16a34a',
    },
    accordionRenew: {
        borderLeftColor: '#2563eb',
    },
    analyticsBar: {
        display: 'flex',
        alignItems: 'stretch',
        flexWrap: 'wrap',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
    },
    analyticsRate: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: theme.spacing(1, 2),
        minWidth: 168,
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    analyticsRateValue: {
        fontSize: 28,
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: '-0.03em',
    },
    analyticsRateMeta: {
        marginTop: 2,
        fontSize: 12,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    analyticsMetrics: {
        display: 'flex',
        flex: '1 1 auto',
        flexWrap: 'wrap',
        alignItems: 'stretch',
    },
    analyticsMetric: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: theme.spacing(0.75, 1.5),
        minWidth: 92,
        borderRight: `1px solid ${theme.palette.divider}`,
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#f8fafc',
        },
    },
    analyticsMetricSelected: {
        backgroundColor: '#eff6ff',
    },
    analyticsMetricValue: {
        fontSize: 18,
        fontWeight: 700,
        lineHeight: 1.1,
        fontVariantNumeric: 'tabular-nums',
    },
    analyticsMetricLabel: {
        marginTop: 2,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    portfolio: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        alignItems: 'stretch',
    },
    hero: {
        flex: '1 1 260px',
        minWidth: 220,
        padding: theme.spacing(2, 2.25),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    heroValue: {
        fontSize: 42,
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: '-0.03em',
    },
    heroLabel: {
        marginTop: theme.spacing(0.75),
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    heroHint: {
        marginTop: theme.spacing(1),
        maxWidth: 320,
        lineHeight: 1.45,
    },
    exceptionGrid: {
        flex: '2 1 460px',
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
    },
    horizonRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    sectionLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    tagList: {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
    },
    tagHeader: {
        display: 'grid',
        gridTemplateColumns:
            'minmax(140px, 1.4fr) repeat(6, minmax(64px, 0.7fr))',
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 1.5),
        backgroundColor: '#f8fafc',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tagRow: {
        display: 'grid',
        gridTemplateColumns:
            'minmax(140px, 1.4fr) repeat(6, minmax(64px, 0.7fr))',
        gap: theme.spacing(1),
        width: '100%',
        padding: theme.spacing(0.5, 1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        textAlign: 'left',
        cursor: 'pointer',
        '&:last-child': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: '#f8fafc',
        },
    },
    tagRowSelected: {
        backgroundColor: '#eff6ff',
    },
    tagName: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
        fontWeight: 600,
    },
    tagDot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        flexShrink: 0,
    },
    tagToggle: {
        justifyContent: 'flex-start',
        padding: theme.spacing(0.25, 1),
    },
    columnHeader: {
        display: 'grid',
        gridTemplateColumns: 'minmax(160px, 1fr) 72px repeat(4, 76px)',
        gap: theme.spacing(1),
        padding: theme.spacing(0, 6, 0.5, 0.5),
    },
    columnLabel: {
        textAlign: 'right',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    metricCell: {
        textAlign: 'right',
        fontSize: 13,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
    },
    metricMuted: {
        color: theme.palette.text.disabled,
        fontWeight: 600,
    },
    summaryContent: {
        display: 'grid',
        gridTemplateColumns: 'minmax(160px, 1fr) 72px repeat(4, 76px)',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        minWidth: 0,
    },
    propertyTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        minWidth: 0,
        flex: '1 1 auto',
    },
    propertyMark: {
        width: 34,
        height: 34,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    propertyMarkDanger: {
        backgroundColor: '#fef2f2',
        color: '#b91c1c',
    },
    propertyMarkWarning: {
        backgroundColor: '#fffbeb',
        color: '#b45309',
    },
    propertyMarkComplete: {
        backgroundColor: '#f0fdf4',
        color: '#15803d',
    },
    propertyIcon: {
        fontSize: 18,
    },
    propertyCopy: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 0,
        flex: '1 1 auto',
    },
    propertyName: {
        fontWeight: 600,
        lineHeight: 1.3,
    },
    propertyMeta: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    stats: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexShrink: 0,
    },
    progressSummary: {
        width: 108,
    },
    progressTrack: {
        height: 6,
        borderRadius: 999,
        backgroundColor: theme.palette.grey[200],
    },
    progressBarDanger: {
        '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            backgroundColor: '#dc2626',
        },
    },
    progressBarWarning: {
        '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            backgroundColor: '#d97706',
        },
    },
    progressBarComplete: {
        '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            backgroundColor: '#16a34a',
        },
    },
    statusChip: {
        width: 148,
        height: 24,
        fontWeight: 700,
        justifyContent: 'center',
        '& .MuiChip-label': {
            width: '100%',
            paddingLeft: 8,
            paddingRight: 8,
            textAlign: 'center',
        },
    },
    gapChip: {
        backgroundColor: '#fef2f2',
        color: '#991b1b',
    },
    warningChip: {
        backgroundColor: '#fffbeb',
        color: '#92400e',
    },
    completeChip: {
        backgroundColor: '#f0fdf4',
        color: '#166534',
    },
    renewChip: {
        backgroundColor: '#eff6ff',
        color: '#1d4ed8',
    },
    details: {
        display: 'block',
        padding: theme.spacing(0, 2, 2),
    },
    categorySection: {
        marginBottom: theme.spacing(1.75),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    categoryHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 0.25, 0.75),
    },
    categoryTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    categoryIcon: {
        fontSize: 16,
        color: theme.palette.text.secondary,
    },
    categoryLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    categoryMeta: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        textAlign: 'right',
    },
    typeList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },
    typeRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 240px) 140px 120px minmax(160px, 1fr)',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1, 1.25),
        borderRadius: 10,
        border: '1px solid rgba(15, 23, 42, 0.06)',
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
        },
    },
    typeRowMissing: {
        backgroundColor: '#fff7f7',
        borderColor: '#fecaca',
    },
    typeRowExpired: {
        backgroundColor: '#fef2f2',
        borderColor: '#fecaca',
    },
    typeRowExpiring: {
        backgroundColor: '#fffbeb',
        borderColor: '#fde68a',
    },
    typeRowRenew: {
        backgroundColor: '#eff6ff',
        borderColor: '#bfdbfe',
    },
    typeRowOnFile: {
        backgroundColor: '#f8fafc',
    },
    typeName: {
        fontWeight: 600,
        minWidth: 0,
    },
    rowChip: {
        width: 140,
        height: 24,
        fontWeight: 700,
        fontSize: 12,
        justifyContent: 'center',
        '& .MuiChip-label': {
            width: '100%',
            paddingLeft: 8,
            paddingRight: 8,
            textAlign: 'center',
        },
    },
    endDateBlock: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    endDateLabel: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
    },
    endDate: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    endDateUrgent: {
        color: '#991b1b',
    },
    endDateSoon: {
        color: '#92400e',
    },
    endDateRenew: {
        color: '#1d4ed8',
    },
    propertyMarkRenew: {
        backgroundColor: '#eff6ff',
        color: '#1d4ed8',
    },
    fileList: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    extraCount: {
        flexShrink: 0,
        minWidth: 28,
        height: 22,
        padding: theme.spacing(0, 0.75),
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.grey[100],
        border: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
        },
    },
    olderPopover: {
        padding: theme.spacing(1),
        width: 280,
        maxWidth: '80vw',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },
    olderFile: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        width: '100%',
        padding: theme.spacing(0.75, 1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        textAlign: 'left',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    },
    olderFileCopy: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        flex: '1 1 auto',
    },
    documentLink: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(0.75),
        flex: '1 1 auto',
        minWidth: 0,
        padding: theme.spacing(0.5, 0.75),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    },
    documentName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 500,
    },
    filePlaceholder: {
        fontSize: 12,
        color: theme.palette.text.disabled,
        paddingLeft: theme.spacing(0.5),
    },
}))

const mostRecentDocument = <T extends { id: number; created_date: string }>(
    documents: T[],
): T | null => {
    if (documents.length === 0) return null
    return documents.reduce((latest, doc) => {
        const latestTime = Date.parse(latest.created_date) || 0
        const docTime = Date.parse(doc.created_date) || 0
        if (docTime !== latestTime) return docTime > latestTime ? doc : latest
        return doc.id > latest.id ? doc : latest
    })
}

const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return ''
    return formatCalendarDate(dateString)
}

type LifecycleRank = 0 | 1 | 2 | 3

const lifecycleRank = (
    status: ContractLifecycleStatus | undefined,
): LifecycleRank => {
    if (status === 'expired') return 0
    if (status === 'ending_soon' || status === 'auto_renew_notice') return 1
    return 3
}

interface RequiredTypeRow {
    typeId: number
    typeName: string
    typeDescription?: string | null
    category: 'contract' | 'certificate' | 'document'
    has_document: boolean
    worstLifecycle: ContractLifecycleStatus | undefined
    worstEndDate: string | null | undefined
    documents: Array<{
        id: number
        name: string
        document: string
        created_date: string
        end_date?: string | null
        lifecycle_status?: ContractLifecycleStatus
    }>
}

interface OverviewPropertyGroup {
    propertyId: number
    propertyName: string
    types: RequiredTypeRow[]
    missingCount: number
    expiredCount: number
    expiringCount: number
    autoRenewCount: number
    onFileCount: number
    due30: number
    due60: number
    due90: number
    totalCount: number
    hasAttention: boolean
}

const emptyProperty = (
    propertyId: number,
    propertyName: string,
): OverviewPropertyGroup => ({
    propertyId,
    propertyName,
    types: [],
    missingCount: 0,
    expiredCount: 0,
    expiringCount: 0,
    autoRenewCount: 0,
    onFileCount: 0,
    due30: 0,
    due60: 0,
    due90: 0,
    totalCount: 0,
    hasAttention: false,
})

const daysUntilEnd = (dateString: string | null | undefined): number | null => {
    if (!dateString) return null
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString)
    if (!match) return null
    const end = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
    )
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return Math.round((end.getTime() - today.getTime()) / 86400000)
}

const rowStatus = (row: RequiredTypeRow): RowStatus => {
    if (!row.has_document) return 'missing'
    if (row.worstLifecycle === 'expired') return 'expired'
    if (row.worstLifecycle === 'auto_renew_notice') return 'auto_renew'
    if (row.worstLifecycle === 'ending_soon') return 'expiring'
    return 'on_file'
}

const addRowCounts = (entry: OverviewPropertyGroup, row: RequiredTypeRow) => {
    entry.totalCount += 1
    const status = rowStatus(row)
    if (status === 'missing') entry.missingCount += 1
    else if (status === 'expired') entry.expiredCount += 1
    else if (status === 'expiring') entry.expiringCount += 1
    else if (status === 'auto_renew') entry.autoRenewCount += 1
    else entry.onFileCount += 1

    if (status !== 'missing' && status !== 'expired') {
        const days = daysUntilEnd(row.worstEndDate)
        if (days != null && days >= 0) {
            if (days <= 90) entry.due90 += 1
            if (days <= 60) entry.due60 += 1
            if (days <= 30) entry.due30 += 1
        }
    }
    entry.hasAttention =
        entry.missingCount > 0 ||
        entry.expiredCount > 0 ||
        entry.expiringCount > 0
}

const statusLabel = (status: RowStatus): string => {
    if (status === 'missing') return 'Missing'
    if (status === 'expired') return 'Expired'
    if (status === 'expiring') return 'Expiring soon'
    if (status === 'auto_renew') return 'Auto-renew'
    return 'On file'
}

const statusRank = (status: RowStatus): number => {
    if (status === 'missing') return 0
    if (status === 'expired') return 1
    if (status === 'auto_renew') return 2
    if (status === 'expiring') return 3
    return 4
}

const propertyTone = (
    group: OverviewPropertyGroup,
): 'danger' | 'warning' | 'renew' | 'complete' => {
    if (group.missingCount > 0 || group.expiredCount > 0) return 'danger'
    if (group.expiringCount > 0) return 'warning'
    if (group.autoRenewCount > 0) return 'renew'
    return 'complete'
}

const compliantCount = (group: {
    onFileCount: number
    autoRenewCount: number
}): number => group.onFileCount + group.autoRenewCount

const compliancePercent = (group: {
    onFileCount: number
    autoRenewCount: number
    totalCount: number
}): number => {
    if (group.totalCount === 0) return 100
    return Math.round((compliantCount(group) / group.totalCount) * 100)
}

const narrowGroup = (
    group: OverviewPropertyGroup,
    category: OverviewCategory,
): OverviewPropertyGroup | null => {
    const types =
        category === 'all'
            ? group.types
            : group.types.filter((type) => type.category === category)
    if (types.length === 0) return null
    const next = emptyProperty(group.propertyId, group.propertyName)
    next.types = types
    types.forEach((type) => addRowCounts(next, type))
    return next
}

function buildOverviewGroups(
    contractCoverage: OrganizationContractTypeCoverage[],
    certificateCoverage: OrganizationContractTypeCoverage[],
    documentCoverage: OrganizationDocumentTypeCoverage[],
): OverviewPropertyGroup[] {
    const propertyMap = new Map<number, OverviewPropertyGroup>()

    const ensureEntry = (id: number, name: string): OverviewPropertyGroup => {
        let entry = propertyMap.get(id)
        if (!entry) {
            entry = emptyProperty(id, name)
            propertyMap.set(id, entry)
        }
        return entry
    }

    const processGroup = (
        typeId: number,
        typeName: string,
        typeDescription: string | null | undefined,
        category: 'contract' | 'certificate' | 'document',
        properties: Array<{
            id: number
            name: string
            is_required: boolean
            has_document: boolean
            documents: Array<
                | OrganizationContractTypeCoverageDocument
                | OrganizationDocumentTypeCoverageDocument
            >
        }>,
    ) => {
        for (const property of properties) {
            if (!property.is_required) continue
            const entry = ensureEntry(property.id, property.name)

            let worstRank: number | null = null
            let worstLifecycle: ContractLifecycleStatus | undefined
            let worstEndDate: string | null | undefined
            if (category !== 'document') {
                for (const doc of property.documents) {
                    const dated = doc as OrganizationContractTypeCoverageDocument
                    const rank = lifecycleRank(dated.lifecycle_status)
                    const earlierDate =
                        !!dated.end_date &&
                        (!worstEndDate || dated.end_date < worstEndDate)
                    if (
                        worstRank === null ||
                        rank < worstRank ||
                        (rank === worstRank && earlierDate)
                    ) {
                        worstRank = rank
                        worstLifecycle = dated.lifecycle_status
                        worstEndDate = dated.end_date
                    }
                }
            }

            const row: RequiredTypeRow = {
                typeId,
                typeName,
                typeDescription,
                category,
                has_document: property.has_document,
                worstLifecycle,
                worstEndDate,
                documents: property.documents.map((doc) => ({
                    id: doc.id,
                    name: doc.name,
                    document: doc.document,
                    created_date: doc.created_date,
                    end_date: (doc as OrganizationContractTypeCoverageDocument)
                        .end_date,
                    lifecycle_status: (doc as OrganizationContractTypeCoverageDocument)
                        .lifecycle_status,
                })),
            }

            entry.types.push(row)
            addRowCounts(entry, row)
        }
    }

    for (const group of contractCoverage) {
        processGroup(
            group.contract_type.id,
            group.contract_type.name,
            group.contract_type.description,
            'contract',
            group.properties,
        )
    }
    for (const group of certificateCoverage) {
        processGroup(
            group.contract_type.id,
            group.contract_type.name,
            group.contract_type.description,
            'certificate',
            group.properties,
        )
    }
    for (const group of documentCoverage) {
        processGroup(
            group.document_type.id,
            group.document_type.name,
            group.document_type.description,
            'document',
            group.properties,
        )
    }

    const result = Array.from(propertyMap.values())
    result.sort((a, b) => {
        const rateDiff = compliancePercent(a) - compliancePercent(b)
        if (rateDiff !== 0) return rateDiff
        return a.propertyName.localeCompare(b.propertyName)
    })

    return result
}

interface OrganizationCoverageOverviewProps {
    organization: number | null
    selectedTagId: number | null
    onSelectedTagIdChange: (value: number | null) => void
}

export const OrganizationCoverageOverview: React.FC<OrganizationCoverageOverviewProps> = ({
    organization,
    selectedTagId,
    onSelectedTagIdChange,
}) => {
    const classes = useStyles()
    const { userTags } = useUserTags()

    const [propertySearch, setPropertySearch] = useState('')
    const [focus, setFocus] = useState<OverviewFocus>('all')
    const [horizon, setHorizon] = useState<HorizonWindow | null>(null)
    const [category, setCategory] = useState<OverviewCategory>('all')
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [expandedIds, setExpandedIds] = useState<number[]>([])
    const [tagsExpanded, setTagsExpanded] = useState(false)
    const listRef = useRef<HTMLDivElement>(null)
    const [previewDoc, setPreviewDoc] = useState<{
        title: string
        fileUrl: string
    } | null>(null)

    const {
        coverage: contractCoverage,
        isLoading: loadingContracts,
    } = useOrganizationContractTypeCoverage({
        organization,
        includeInactive: false,
    })
    const {
        coverage: certificateCoverage,
        isLoading: loadingCertificates,
    } = useOrganizationCertificateTypeCoverage({
        organization,
        includeInactive: false,
    })
    const {
        coverage: documentCoverage,
        isLoading: loadingDocuments,
    } = useOrganizationDocumentTypeCoverage({
        organization,
        includeInactive: false,
    })

    const isLoading =
        loadingContracts || loadingCertificates || loadingDocuments

    const allGroups = useMemo(
        () =>
            buildOverviewGroups(
                contractCoverage,
                certificateCoverage,
                documentCoverage,
            ),
        [contractCoverage, certificateCoverage, documentCoverage],
    )

    const filteredGroups = useMemo(() => {
        let groups = allGroups

        if (selectedTagId != null) {
            const tag = userTags.find((item) => item.id === selectedTagId)
            if (tag) {
                const tagPropertyIds = new Set(tag.apartments)
                groups = groups.filter((group) =>
                    tagPropertyIds.has(group.propertyId),
                )
            }
        }

        const search = propertySearch.trim().toLowerCase()
        if (search) {
            groups = groups.filter((group) =>
                group.propertyName.toLowerCase().includes(search),
            )
        }
        return groups
            .map((group) => narrowGroup(group, category))
            .filter((group): group is OverviewPropertyGroup => group != null)
            .sort(
                (a, b) =>
                    compliancePercent(a) - compliancePercent(b) ||
                    a.propertyName.localeCompare(b.propertyName),
            )
    }, [allGroups, selectedTagId, userTags, propertySearch, category])

    const visibleGroups = useMemo(() => {
        let groups = filteredGroups
        if (focus === 'attention') {
            groups = groups.filter((group) => group.hasAttention)
        } else if (focus === 'missing') {
            groups = groups.filter((group) => group.missingCount > 0)
        } else if (focus === 'expired') {
            groups = groups.filter((group) => group.expiredCount > 0)
        } else if (focus === 'expiring') {
            groups = groups.filter((group) => group.expiringCount > 0)
        } else if (focus === 'auto_renew') {
            groups = groups.filter((group) => group.autoRenewCount > 0)
        } else if (focus === 'current') {
            groups = groups.filter((group) => !group.hasAttention)
        }
        if (horizon === 30) groups = groups.filter((group) => group.due30 > 0)
        else if (horizon === 60)
            groups = groups.filter((group) => group.due60 > 0)
        else if (horizon === 90)
            groups = groups.filter((group) => group.due90 > 0)
        return groups
    }, [filteredGroups, focus, horizon])

    const safePage = Math.min(
        page,
        Math.max(0, Math.ceil(visibleGroups.length / rowsPerPage) - 1),
    )
    const pagedGroups = useMemo(() => {
        const start = safePage * rowsPerPage
        return visibleGroups.slice(start, start + rowsPerPage)
    }, [visibleGroups, safePage, rowsPerPage])

    useEffect(() => {
        setPage(0)
    }, [propertySearch, focus, category, selectedTagId, horizon])

    const filterChips = useMemo<FilterChipItem[]>(() => {
        const next: FilterChipItem[] = []
        if (focus !== 'all') {
            next.push({
                id: 'status',
                label: `Status: ${FOCUS_LABELS[focus]}`,
                onDelete: () => setFocus('all'),
            })
        }
        if (horizon != null) {
            next.push({
                id: 'horizon',
                label: `Ends within ${horizon} days`,
                onDelete: () => setHorizon(null),
            })
        }
        if (category !== 'all') {
            next.push({
                id: 'category',
                label: CATEGORY_LABELS[category],
                onDelete: () => setCategory('all'),
            })
        }
        const tagChip = getPropertyTagChip(selectedTagId, userTags, () =>
            onSelectedTagIdChange(null),
        )
        if (tagChip) next.push(tagChip)
        return next
    }, [
        focus,
        horizon,
        category,
        selectedTagId,
        userTags,
        onSelectedTagIdChange,
    ])

    const clearDrawerFilters = () => {
        setFocus('all')
        setHorizon(null)
        setCategory('all')
        onSelectedTagIdChange(null)
    }

    const selectFocus = (next: OverviewFocus) => {
        setFocus((current) => (current === next ? 'all' : next))
    }

    const stats = useMemo(() => {
        return filteredGroups.reduce(
            (acc, group) => {
                acc.properties += 1
                acc.missing += group.missingCount
                acc.expired += group.expiredCount
                acc.expiring += group.expiringCount
                acc.autoRenew += group.autoRenewCount
                acc.onFile += group.onFileCount
                acc.total += group.totalCount
                acc.due30 += group.due30
                acc.due60 += group.due60
                acc.due90 += group.due90
                acc.attentionProperties += group.hasAttention ? 1 : 0
                return acc
            },
            {
                properties: 0,
                missing: 0,
                expired: 0,
                expiring: 0,
                autoRenew: 0,
                onFile: 0,
                total: 0,
                due30: 0,
                due60: 0,
                due90: 0,
                attentionProperties: 0,
            },
        )
    }, [filteredGroups])

    const tagRollups = useMemo(() => {
        return userTags
            .map((tag) => {
                const propertyIds = new Set(tag.apartments)
                const properties = allGroups
                    .filter((group) => propertyIds.has(group.propertyId))
                    .map((group) => narrowGroup(group, category))
                    .filter(
                        (group): group is OverviewPropertyGroup =>
                            group != null,
                    )
                const totals = properties.reduce(
                    (acc, group) => {
                        acc.missing += group.missingCount
                        acc.expired += group.expiredCount
                        acc.expiring += group.expiringCount
                        acc.autoRenew += group.autoRenewCount
                        acc.onFile += group.onFileCount
                        acc.total += group.totalCount
                        return acc
                    },
                    {
                        missing: 0,
                        expired: 0,
                        expiring: 0,
                        autoRenew: 0,
                        onFile: 0,
                        total: 0,
                    },
                )
                return {
                    id: tag.id,
                    name: tag.name,
                    color: tag.color,
                    properties: properties.length,
                    ...totals,
                    rate: compliancePercent({
                        onFileCount: totals.onFile,
                        autoRenewCount: totals.autoRenew,
                        totalCount: totals.total,
                    }),
                }
            })
            .filter((tag) => tag.properties > 0)
            .sort((a, b) => a.rate - b.rate || a.name.localeCompare(b.name))
    }, [userTags, allGroups, category])

    const visibleTagRollups = useMemo(() => {
        const previewCount = 5
        if (tagsExpanded || tagRollups.length <= previewCount) return tagRollups
        const preview = tagRollups.slice(0, previewCount)
        if (
            selectedTagId == null ||
            preview.some((tag) => tag.id === selectedTagId)
        ) {
            return preview
        }
        const selected = tagRollups.find((tag) => tag.id === selectedTagId)
        return selected ? [...preview, selected] : preview
    }, [tagRollups, tagsExpanded, selectedTagId])

    const attentionItems = stats.missing + stats.expired + stats.expiring
    const covered = compliantCount({
        onFileCount: stats.onFile,
        autoRenewCount: stats.autoRenew,
    })
    const compliance = compliancePercent({
        onFileCount: stats.onFile,
        autoRenewCount: stats.autoRenew,
        totalCount: stats.total,
    })

    const toggleExpanded = (propertyId: number) => {
        setExpandedIds((current) =>
            current.includes(propertyId)
                ? current.filter((id) => id !== propertyId)
                : [...current, propertyId],
        )
    }

    const expandIssues = () => {
        setFocus('all')
        setExpandedIds(
            filteredGroups
                .filter((group) => group.hasAttention)
                .map((group) => group.propertyId),
        )
    }

    if (isLoading) {
        return (
            <Paper variant="outlined" className={classes.loadingContainer}>
                <CircularProgress />
            </Paper>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.analyticsBar}>
                <Tooltip title={COMPLIANCE_TOOLTIP}>
                    <div className={classes.analyticsRate}>
                        <Typography
                            className={`${classes.analyticsRateValue} ${
                                compliance < 70
                                    ? classes.statDanger
                                    : compliance < 90
                                    ? classes.statWarning
                                    : classes.statSuccess
                            }`}
                        >
                            {compliance}%
                        </Typography>
                        <Typography className={classes.analyticsRateMeta}>
                            {covered} of {stats.total} compliant
                        </Typography>
                    </div>
                </Tooltip>
                <Box className={classes.analyticsMetrics}>
                    {([
                        [
                            'missing',
                            'Missing',
                            stats.missing,
                            classes.statDanger,
                        ],
                        [
                            'expired',
                            'Expired',
                            stats.expired,
                            classes.statDanger,
                        ],
                        [
                            'expiring',
                            'Expiring',
                            stats.expiring,
                            classes.statWarning,
                        ],
                        [
                            'auto_renew',
                            'Auto-renew',
                            stats.autoRenew,
                            classes.statRenew,
                        ],
                    ] as Array<[OverviewFocus, string, number, string]>).map(
                        ([id, label, value, valueClass]) => (
                            <ButtonBase
                                key={id}
                                className={`${classes.analyticsMetric} ${
                                    focus === id
                                        ? classes.analyticsMetricSelected
                                        : ''
                                }`}
                                onClick={() => selectFocus(id)}
                            >
                                <Typography
                                    className={`${
                                        classes.analyticsMetricValue
                                    } ${
                                        value > 0
                                            ? valueClass
                                            : classes.statSuccess
                                    }`}
                                >
                                    {value}
                                </Typography>
                                <Typography
                                    className={classes.analyticsMetricLabel}
                                >
                                    {label}
                                </Typography>
                            </ButtonBase>
                        ),
                    )}
                    {([30, 60, 90] as HorizonWindow[]).map((window) => {
                        const value =
                            window === 30
                                ? stats.due30
                                : window === 60
                                ? stats.due60
                                : stats.due90
                        return (
                            <ButtonBase
                                key={window}
                                className={`${classes.analyticsMetric} ${
                                    horizon === window
                                        ? classes.analyticsMetricSelected
                                        : ''
                                }`}
                                onClick={() =>
                                    setHorizon((current) =>
                                        current === window ? null : window,
                                    )
                                }
                            >
                                <Typography
                                    className={`${
                                        classes.analyticsMetricValue
                                    } ${
                                        value > 0
                                            ? classes.statWarning
                                            : classes.statSuccess
                                    }`}
                                >
                                    {value}
                                </Typography>
                                <Typography
                                    className={classes.analyticsMetricLabel}
                                >
                                    {window} days
                                </Typography>
                            </ButtonBase>
                        )
                    })}
                </Box>
            </Box>

            {tagRollups.length > 0 && (
                <Box className={classes.tagList}>
                    <Box className={classes.tagHeader}>
                        <Typography
                            className={classes.columnLabel}
                            style={{ textAlign: 'left' }}
                        >
                            Tag
                        </Typography>
                        {[
                            'Properties',
                            'Compliant',
                            'Missing',
                            'Expired',
                            'Expiring',
                            'Auto-renew',
                        ].map((label) => (
                            <Typography
                                key={label}
                                className={classes.columnLabel}
                            >
                                {label}
                            </Typography>
                        ))}
                    </Box>
                    {visibleTagRollups.map((tag) => (
                        <Box
                            key={tag.id}
                            className={`${classes.tagRow} ${
                                selectedTagId === tag.id
                                    ? classes.tagRowSelected
                                    : ''
                            }`}
                            onClick={() =>
                                onSelectedTagIdChange(
                                    selectedTagId === tag.id ? null : tag.id,
                                )
                            }
                        >
                            <Box className={classes.tagName}>
                                <Box
                                    className={classes.tagDot}
                                    style={{ backgroundColor: tag.color }}
                                />
                                <Typography variant="body2" noWrap>
                                    {tag.name}
                                </Typography>
                            </Box>
                            <Typography className={classes.metricCell}>
                                {tag.properties}
                            </Typography>
                            <Tooltip title={COMPLIANCE_TOOLTIP}>
                                <Typography
                                    className={`${classes.metricCell} ${
                                        tag.rate < 70
                                            ? classes.statDanger
                                            : tag.rate < 90
                                            ? classes.statWarning
                                            : classes.statSuccess
                                    }`}
                                >
                                    {tag.rate}%
                                </Typography>
                            </Tooltip>
                            <CountCell
                                value={tag.missing}
                                classes={classes}
                                danger
                            />
                            <CountCell
                                value={tag.expired}
                                classes={classes}
                                danger
                            />
                            <CountCell value={tag.expiring} classes={classes} />
                            <CountCell
                                value={tag.autoRenew}
                                classes={classes}
                            />
                        </Box>
                    ))}
                    {tagRollups.length > 5 && (
                        <Button
                            size="small"
                            className={`${classes.textButton} ${classes.tagToggle}`}
                            onClick={() => setTagsExpanded((open) => !open)}
                        >
                            {tagsExpanded
                                ? 'Show fewer tags'
                                : `Show ${
                                      tagRollups.length -
                                      visibleTagRollups.length
                                  } more tags`}
                        </Button>
                    )}
                </Box>
            )}

            <CatalogFilterBar
                searchPlaceholder="Search properties..."
                searchValue={propertySearch}
                onSearchChange={setPropertySearch}
                onOpenFilters={() => setDrawerOpen(true)}
                chips={filterChips}
                onClearAll={
                    filterChips.length > 0 ? clearDrawerFilters : undefined
                }
                resultLabel={`${visibleGroups.length} ${
                    visibleGroups.length === 1 ? 'property' : 'properties'
                }`}
                toolbarExtra={
                    <>
                        <Button
                            size="small"
                            className={classes.textButton}
                            onClick={expandIssues}
                            disabled={attentionItems === 0}
                        >
                            Expand issues
                        </Button>
                        <Button
                            size="small"
                            className={classes.textButton}
                            onClick={() => setExpandedIds([])}
                            disabled={expandedIds.length === 0}
                        >
                            Collapse
                        </Button>
                    </>
                }
            />
            <CatalogFilterDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title="Overview filters"
                activeCount={filterChips.length}
                onClear={clearDrawerFilters}
            >
                <PropertyTagFilterField
                    value={selectedTagId}
                    onChange={onSelectedTagIdChange}
                />
                <CatalogSelectField
                    label="Status"
                    value={focus}
                    onChange={(value) => setFocus(value as OverviewFocus)}
                    options={([
                        'all',
                        'attention',
                        'missing',
                        'expired',
                        'expiring',
                        'auto_renew',
                        'current',
                    ] as OverviewFocus[]).map((value) => ({
                        value,
                        label: FOCUS_LABELS[value],
                    }))}
                />
                <CatalogSelectField
                    label="Requirements"
                    value={category}
                    onChange={(value) => setCategory(value as OverviewCategory)}
                    options={([
                        'all',
                        'contract',
                        'certificate',
                        'document',
                    ] as OverviewCategory[]).map((value) => ({
                        value,
                        label: CATEGORY_LABELS[value],
                    }))}
                />
            </CatalogFilterDrawer>

            {visibleGroups.length === 0 ? (
                <Paper variant="outlined" className={classes.emptyState}>
                    <HomeWorkIcon className={classes.emptyIcon} />
                    <Typography variant="subtitle1">
                        {allGroups.length === 0
                            ? 'No required types yet'
                            : 'No properties match'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {allGroups.length === 0
                            ? 'Create a contract, certificate, or document type and choose the properties that require it.'
                            : 'Try another search, or change the filters.'}
                    </Typography>
                </Paper>
            ) : (
                <div className={classes.list} ref={listRef}>
                    <Box className={classes.columnHeader}>
                        <span />
                        {[
                            'Rate',
                            'Missing',
                            'Expired',
                            'Expiring',
                            'Auto-renew',
                        ].map((label) => (
                            <Typography
                                key={label}
                                className={classes.columnLabel}
                            >
                                {label}
                            </Typography>
                        ))}
                    </Box>
                    {pagedGroups.map((group) => {
                        const tone = propertyTone(group)
                        const percent = compliancePercent(group)
                        const contractTypes = group.types.filter(
                            (type) => type.category === 'contract',
                        )
                        const certificateTypes = group.types.filter(
                            (type) => type.category === 'certificate',
                        )
                        const documentTypes = group.types.filter(
                            (type) => type.category === 'document',
                        )

                        return (
                            <Accordion
                                key={group.propertyId}
                                className={`${classes.accordion} ${
                                    tone === 'danger'
                                        ? classes.accordionDanger
                                        : tone === 'warning'
                                        ? classes.accordionWarning
                                        : tone === 'renew'
                                        ? classes.accordionRenew
                                        : classes.accordionComplete
                                }`}
                                expanded={expandedIds.includes(
                                    group.propertyId,
                                )}
                                onChange={() =>
                                    toggleExpanded(group.propertyId)
                                }
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                >
                                    <Box className={classes.summaryContent}>
                                        <Box className={classes.propertyTitle}>
                                            <Box
                                                className={`${
                                                    classes.propertyMark
                                                } ${
                                                    tone === 'danger'
                                                        ? classes.propertyMarkDanger
                                                        : tone === 'warning'
                                                        ? classes.propertyMarkWarning
                                                        : tone === 'renew'
                                                        ? classes.propertyMarkRenew
                                                        : classes.propertyMarkComplete
                                                }`}
                                            >
                                                <HomeWorkIcon
                                                    className={
                                                        classes.propertyIcon
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                className={classes.propertyCopy}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    className={
                                                        classes.propertyName
                                                    }
                                                    noWrap
                                                >
                                                    {group.propertyName}
                                                </Typography>
                                                <Typography
                                                    className={
                                                        classes.propertyMeta
                                                    }
                                                    noWrap
                                                >
                                                    {group.totalCount} required
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Tooltip title={COMPLIANCE_TOOLTIP}>
                                            <Typography
                                                className={`${
                                                    classes.metricCell
                                                } ${
                                                    percent < 70
                                                        ? classes.statDanger
                                                        : percent < 90
                                                        ? classes.statWarning
                                                        : classes.statSuccess
                                                }`}
                                            >
                                                {percent}%
                                            </Typography>
                                        </Tooltip>
                                        <CountCell
                                            value={group.missingCount}
                                            classes={classes}
                                            danger
                                        />
                                        <CountCell
                                            value={group.expiredCount}
                                            classes={classes}
                                            danger
                                        />
                                        <CountCell
                                            value={group.expiringCount}
                                            classes={classes}
                                        />
                                        <CountCell
                                            value={group.autoRenewCount}
                                            classes={classes}
                                        />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails className={classes.details}>
                                    {contractTypes.length > 0 && (
                                        <CategorySection
                                            label="Contracts"
                                            icon={
                                                <WorkIcon
                                                    className={
                                                        classes.categoryIcon
                                                    }
                                                />
                                            }
                                            types={contractTypes}
                                            classes={classes}
                                            onPreview={setPreviewDoc}
                                        />
                                    )}
                                    {certificateTypes.length > 0 && (
                                        <CategorySection
                                            label="Certificates"
                                            icon={
                                                <VerifiedUserIcon
                                                    className={
                                                        classes.categoryIcon
                                                    }
                                                />
                                            }
                                            types={certificateTypes}
                                            classes={classes}
                                            onPreview={setPreviewDoc}
                                        />
                                    )}
                                    {documentTypes.length > 0 && (
                                        <CategorySection
                                            label="Documents"
                                            icon={
                                                <DescriptionIcon
                                                    className={
                                                        classes.categoryIcon
                                                    }
                                                />
                                            }
                                            types={documentTypes}
                                            classes={classes}
                                            onPreview={setPreviewDoc}
                                        />
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        )
                    })}
                </div>
            )}

            {visibleGroups.length > 0 && (
                <TablePagination
                    className={classes.pagination}
                    component="div"
                    labelRowsPerPage="Properties per page"
                    rowsPerPageOptions={[10, 25, 50]}
                    count={visibleGroups.length}
                    rowsPerPage={rowsPerPage}
                    page={safePage}
                    onChangePage={(_event, nextPage) => {
                        setPage(nextPage)
                        listRef.current?.scrollIntoView({
                            block: 'start',
                            behavior: 'smooth',
                        })
                    }}
                    onChangeRowsPerPage={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10))
                        setPage(0)
                    }}
                />
            )}

            <DocumentPreviewModal
                open={previewDoc != null}
                onClose={() => setPreviewDoc(null)}
                title={previewDoc?.title || 'Document'}
                fileUrl={previewDoc?.fileUrl || null}
            />
        </Box>
    )
}

const CountCell: React.FC<{
    value: number
    classes: ReturnType<typeof useStyles>
    danger?: boolean
}> = ({ value, classes, danger }) => (
    <Typography
        className={`${classes.metricCell} ${
            value === 0
                ? classes.metricMuted
                : danger
                ? classes.statDanger
                : classes.statWarning
        }`}
    >
        {value}
    </Typography>
)

const CategorySection: React.FC<{
    label: string
    icon: React.ReactNode
    types: RequiredTypeRow[]
    classes: ReturnType<typeof useStyles>
    onPreview: (doc: { title: string; fileUrl: string }) => void
}> = ({ label, icon, types, classes, onPreview }) => {
    const [olderMenu, setOlderMenu] = useState<{
        anchorEl: HTMLElement
        files: RequiredTypeRow['documents']
    } | null>(null)
    const ordered = [...types].sort((a, b) => {
        const diff = statusRank(rowStatus(a)) - statusRank(rowStatus(b))
        if (diff !== 0) return diff
        return a.typeName.localeCompare(b.typeName)
    })
    const summaryParts = ([
        'missing',
        'expired',
        'auto_renew',
        'expiring',
        'on_file',
    ] as RowStatus[])
        .map((status) => {
            const count = ordered.filter((type) => rowStatus(type) === status)
                .length
            if (count === 0) return null
            return `${count} ${statusLabel(status).toLowerCase()}`
        })
        .filter((part): part is string => part != null)

    return (
        <Box className={classes.categorySection}>
            <Box className={classes.categoryHeader}>
                <Box className={classes.categoryTitle}>
                    {icon}
                    <Typography className={classes.categoryLabel}>
                        {label}
                    </Typography>
                </Box>
                <Typography className={classes.categoryMeta}>
                    {summaryParts.join(' · ')}
                </Typography>
            </Box>
            <Box className={classes.typeList}>
                {ordered.map((type) => {
                    const status = rowStatus(type)
                    const endDate = formatDate(type.worstEndDate)
                    const latest = mostRecentDocument(type.documents)
                    const olderFiles = latest
                        ? type.documents
                              .filter((doc) => doc.id !== latest.id)
                              .sort((a, b) => {
                                  const diff =
                                      (Date.parse(b.created_date) || 0) -
                                      (Date.parse(a.created_date) || 0)
                                  if (diff !== 0) return diff
                                  return b.id - a.id
                              })
                        : []
                    const extraCount = olderFiles.length
                    const rowClass =
                        status === 'missing'
                            ? classes.typeRowMissing
                            : status === 'expired'
                            ? classes.typeRowExpired
                            : status === 'expiring'
                            ? classes.typeRowExpiring
                            : status === 'auto_renew'
                            ? classes.typeRowRenew
                            : classes.typeRowOnFile
                    const chipClass =
                        status === 'missing' || status === 'expired'
                            ? classes.gapChip
                            : status === 'expiring'
                            ? classes.warningChip
                            : status === 'auto_renew'
                            ? classes.renewChip
                            : classes.completeChip
                    const dateClass =
                        status === 'expired'
                            ? classes.endDateUrgent
                            : status === 'expiring'
                            ? classes.endDateSoon
                            : status === 'auto_renew'
                            ? classes.endDateRenew
                            : ''

                    return (
                        <Box
                            key={`${type.category}-${type.typeId}`}
                            className={`${classes.typeRow} ${rowClass}`}
                        >
                            <Tooltip
                                title={type.typeDescription || ''}
                                disableHoverListener={!type.typeDescription}
                            >
                                <Typography
                                    variant="body2"
                                    className={classes.typeName}
                                    noWrap
                                >
                                    {type.typeName}
                                </Typography>
                            </Tooltip>
                            <Chip
                                size="small"
                                label={statusLabel(status)}
                                className={`${classes.rowChip} ${chipClass}`}
                            />
                            {status !== 'missing' && endDate ? (
                                <Box className={classes.endDateBlock}>
                                    <Typography
                                        className={classes.endDateLabel}
                                    >
                                        Expires
                                    </Typography>
                                    <Typography
                                        className={`${classes.endDate} ${dateClass}`}
                                    >
                                        {endDate}
                                    </Typography>
                                </Box>
                            ) : (
                                <span />
                            )}
                            <Box className={classes.fileList}>
                                {!latest ? (
                                    <Typography
                                        className={classes.filePlaceholder}
                                    >
                                        Not uploaded
                                    </Typography>
                                ) : latest.document ? (
                                    <Tooltip
                                        title={`Uploaded ${
                                            formatDate(latest.created_date) ||
                                            '—'
                                        }`}
                                    >
                                        <ButtonBase
                                            className={classes.documentLink}
                                            onClick={() =>
                                                onPreview({
                                                    title: latest.name,
                                                    fileUrl: latest.document,
                                                })
                                            }
                                        >
                                            <Typography
                                                variant="body2"
                                                className={classes.documentName}
                                            >
                                                {latest.name}
                                            </Typography>
                                            <VisibilityIcon fontSize="small" />
                                        </ButtonBase>
                                    </Tooltip>
                                ) : (
                                    <Typography
                                        className={classes.filePlaceholder}
                                    >
                                        {latest.name}
                                    </Typography>
                                )}
                                {extraCount > 0 && (
                                    <ButtonBase
                                        className={classes.extraCount}
                                        aria-label={`${extraCount} older files`}
                                        onClick={(event) =>
                                            setOlderMenu({
                                                anchorEl: event.currentTarget,
                                                files: olderFiles,
                                            })
                                        }
                                    >
                                        +{extraCount}
                                    </ButtonBase>
                                )}
                            </Box>
                        </Box>
                    )
                })}
            </Box>
            <Popover
                open={olderMenu != null}
                anchorEl={olderMenu?.anchorEl}
                onClose={() => setOlderMenu(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box className={classes.olderPopover}>
                    {olderMenu?.files.map((doc) => (
                        <ButtonBase
                            key={doc.id}
                            className={classes.olderFile}
                            disabled={!doc.document}
                            onClick={() => {
                                if (!doc.document) return
                                onPreview({
                                    title: doc.name,
                                    fileUrl: doc.document,
                                })
                                setOlderMenu(null)
                            }}
                        >
                            <Box className={classes.olderFileCopy}>
                                <Typography
                                    variant="body2"
                                    className={classes.documentName}
                                >
                                    {doc.name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    Uploaded{' '}
                                    {formatDate(doc.created_date) || '—'}
                                </Typography>
                            </Box>
                            <VisibilityIcon fontSize="small" />
                        </ButtonBase>
                    ))}
                </Box>
            </Popover>
        </Box>
    )
}
